import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaChartBar, FaProjectDiagram, FaUsers, FaLayerGroup, FaSitemap, FaCouch, FaClock, FaCertificate, FaUserCheck, FaHistory, FaFileContract, FaTasks, FaUserClock, FaSyncAlt, FaExchangeAlt, FaClone, FaExclamationTriangle, FaCalendarAlt, FaShieldAlt } from 'react-icons/fa';
import { Typography, Grid } from '@mui/material';
import { useAbility } from '../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import { FEATURE_FLAGS } from '../../constants/featureFlags'; // ADR-199
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  MainSection,
  BackButton,
  ReportCard,
  ReportIcon
} from './styles';

const Reports = () => {
  const navigate = useNavigate();
  const ability = useAbility();
  const canViewBenchReport = ability.can(ACTIONS.READ, SUBJECTS.BENCH_REPORT);
  const canViewBenchCandidates = ability.can(ACTIONS.READ, SUBJECTS.BENCH_CANDIDATE); // ADR-107
  const canViewDiscrepancyReport = ability.can(ACTIONS.READ, SUBJECTS.DIRECT_MANAGER_DISCREPANCY);
  const canViewIngestionSyncRuns = ability.can(ACTIONS.READ, SUBJECTS.INGESTION_SYNC_RUN); // ADR-133
  const canViewScheduledJobs = ability.can(ACTIONS.READ, SUBJECTS.SCHEDULER_STATUS); // ADR-198
  const canViewModificationAudit = ability.can(ACTIONS.READ, SUBJECTS.FEEDBACK_MODIFICATION_AUDIT_REPORT);
  const canViewWorkforceComposition = ability.can(ACTIONS.READ, SUBJECTS.WORKFORCE_COMPOSITION_REPORT); // ADR-029
  const canViewFixedTermContracts = ability.can(ACTIONS.READ, SUBJECTS.FIXED_TERM_CONTRACT_REPORT); // ADR-090
  const canViewAssignmentCompletion = ability.can(ACTIONS.READ, SUBJECTS.ASSIGNMENT_COMPLETION_REPORT); // ADR-093
  const canViewOrganizationHierarchy = ability.can(ACTIONS.READ, SUBJECTS.ORGANIZATION_HIERARCHY_REPORT); // ADR-096
  const canViewEmploymentChangeLog = ability.can(ACTIONS.READ, SUBJECTS.EMPLOYMENT_CHANGE_RECORD); // ADR-145
  const canViewOverlappingLegs = ability.can(ACTIONS.MANAGE, SUBJECTS.EMPLOYMENT_DATA_REPAIR); // ADR-165
  const canViewEpisodeDiscrepancies = ability.can(ACTIONS.READ, SUBJECTS.EMPLOYMENT_EPISODE_DISCREPANCY_REPORT); // ADR-166
  // ADR-199: hidden behind a flag until the check rules are reviewed.
  const canViewIntegrityChecks = FEATURE_FLAGS.INTEGRITY_CHECKS_REPORT && canViewEmploymentChangeLog;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Reports</Title>
        </HeaderLeft>
      </Header>
      
      <Content>
        <MainSection>
          {/*
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ReportCard onClick={() => navigate('/reports/employee-project-metrics')}>
                <ReportIcon>
                  <FaChartBar />
                </ReportIcon>
                <Typography variant="h6" align="center">Employee and Project Metrics</Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  View distribution of employees across projects
                </Typography>
              </ReportCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ReportCard onClick={() => navigate('/reports/project-skills-level')}>
                <ReportIcon>
                  <FaProjectDiagram />
                </ReportIcon>
                <Typography variant="h6" align="center">Project Skills Level</Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  View technology skills distribution within projects
                </Typography>
              </ReportCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ReportCard onClick={() => navigate('/reports/employees-activities')}>
                <ReportIcon>
                  <FaUsers />
                </ReportIcon>
                <Typography variant="h6" align="center">Employees Activities</Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  View employees grouped by business unit, project, or technology
                </Typography>
              </ReportCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ReportCard onClick={() => navigate('/reports/resource-distribution')}>
                <ReportIcon>
                  <FaLayerGroup />
                </ReportIcon>
                <Typography variant="h6" align="center">Resource Distribution</Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  View hierarchical distribution of resources across organization
                </Typography>
              </ReportCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ReportCard onClick={() => navigate('/reports/organization-hierarchy')}>
                <ReportIcon>
                  <FaSitemap />
                </ReportIcon>
                <Typography variant="h6" align="center">Organization Hierarchy</Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  View the complete organizational structure as an interactive tree diagram
                </Typography>
              </ReportCard>
            </Grid>
            */}
          <Grid container spacing={3}>
            {canViewBenchReport && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/bench')}>
                  <ReportIcon>
                    <FaCouch />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Bench Report</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    View individuals currently on bench with filtering by business unit, type, and skills
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewBenchCandidates && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/bench-candidates')}>
                  <ReportIcon>
                    <FaUserClock />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Bench Candidates</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Individuals rolling off or recently left (Americas feed) — assign to a bench project, import, or dismiss
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <ReportCard onClick={() => navigate('/reports/certifications')}>
                <ReportIcon>
                  <FaCertificate />
                </ReportIcon>
                <Typography variant="h6" align="center">Certifications Report</Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  View individuals by certifications with filtering by issuer, category, and expiration status
                </Typography>
              </ReportCard>
            </Grid>
            {canViewOrganizationHierarchy && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/organization-hierarchy')}>
                  <ReportIcon>
                    <FaSitemap />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Organization Hierarchy</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Clusters, Business Units, projects and teams with their owners
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewWorkforceComposition && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/workforce-composition')}>
                  <ReportIcon>
                    <FaUsers />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Workforce Composition</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Individuals by position, grade and role across Clusters, Business Units and projects — with data-coverage insight
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewFixedTermContracts && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/fixed-term-contracts')}>
                  <ReportIcon>
                    <FaFileContract />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Fixed Term Contracts</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Monitor fixed term contract expirations with filtering by cluster, business unit, and expiry window
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewEmploymentChangeLog && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/employment-changes')}>
                  <ReportIcon>
                    <FaExchangeAlt />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Employment Change Log</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    What changed on people&apos;s employment records, when and by whom
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewEpisodeDiscrepancies && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/employment-episode-discrepancies')}>
                  <ReportIcon>
                    <FaExclamationTriangle />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Employment Episode Discrepancies</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Contracts that cannot be right — what is wrong, and where to fix it
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewOverlappingLegs && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/overlapping-position-legs')}>
                  <ReportIcon>
                    <FaClone />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Overlapping Position Legs</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    People holding two positions at once — what the repair can fix, and what needs a hand
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewAssignmentCompletion && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/assignment-completion')}>
                  <ReportIcon>
                    <FaTasks />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Assignment Completion</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Track feedback assignment completion per BU against the expected count, with period, cluster, and below-target filters
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewBenchReport && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/bench-entry-lag')}>
                  <ReportIcon>
                    <FaClock />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Bench Data Entry Lag</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Track the delay between bench start date and system registration for individuals currently on bench
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewDiscrepancyReport && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/direct-manager-discrepancies/runs')}>
                  <ReportIcon>
                    <FaUserCheck />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Direct Representative Discrepancy Audit</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Compare radar's resolved representative against the upstream systems' reported representatives to surface drift
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewIngestionSyncRuns && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/ingestion-sync-runs/runs')}>
                  <ReportIcon>
                    <FaSyncAlt />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Ingestion Sync Runs</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    What each Americas and PowerBI sync did: who was hired, updated, skipped or failed, run by run
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewIntegrityChecks && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/integrity-checks')}>
                  <ReportIcon>
                    <FaShieldAlt />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Integrity Checks</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Run the employment, contract and organization-history rules live and see every violation
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewScheduledJobs && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/scheduled-jobs')}>
                  <ReportIcon>
                    <FaCalendarAlt />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Scheduled Jobs</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Every background job: when it runs, what is running now, and how its latest run ended
                  </Typography>
                </ReportCard>
              </Grid>
            )}
            {canViewModificationAudit && (
              <Grid item xs={12} md={6}>
                <ReportCard onClick={() => navigate('/reports/feedback-session-modifications')}>
                  <ReportIcon>
                    <FaHistory />
                  </ReportIcon>
                  <Typography variant="h6" align="center">Feedback Modification Audit</Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Audit trail of changes to in-progress feedback sessions: who changed what, when, and why
                  </Typography>
                </ReportCard>
              </Grid>
            )}
          </Grid>
        </MainSection>
      </Content>
    </Container>
  );
};

export default Reports; 